// A conversation in a side pane: the Insights drawers, "Help me write this",
// Theme AI's extraction chat. Drawn from the same parts as the main agent
// conversation (Adam, 23 Sep: "style all agent drawers the same as the main
// agent message list"): MessageRow for the reader's and the assistant's turns
// (so assistant text goes through AgentMarkdown), ComposerCard for the input,
// the living circle while a reply is on its way, and the surface.ts tokens for
// every edge and corner.
//
// Presentational only. The host owns the conversation and the network; this
// owns the draft, the dictation and the scroll. Two inputs:
//  - `messages`: text rows. Most side panes talk to a plain chat completion,
//    so every row is text plus, optionally, a host-drawn `extra` (the Tables
//    pane's render_graph charts) under it.
//  - `conversation`: an agent session's own state (a page helper's chat,
//    Douglas plan T8). Every row is the main list's MessageRow with its tool
//    chain, thinking, ask_user cards, artifacts and render plugins, cut to its
//    slice by useRowSlices exactly as AgentChat does.
//
// `readOnly` draws the conversation alone: no composer, no prompts, no draft
// (a delegated agent's conversation, which nobody can write into).
//
// MessageRow is React.memo'd. Every prop it gets from here is a module-level
// constant or a message object whose identity is kept per source message
// (toAgentMessage's WeakMap), so typing in the composer re-renders no row.
import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import MessageRow from './MessageRow.js'
import ComposerCard, { COMPOSER_OVERHANG_PX, type ComposerModel } from './ComposerCard.js'
import ChatInputToolbar from './ChatInputToolbar.js'
import ActivityBadge from './ActivityBadge.js'
import { useComposerDraft } from '../hooks/useComposerDraft.js'
import useVoiceDictation from '../hooks/useVoiceDictation.js'
import { useActivityLog } from '../hooks/useActivityLog.js'
import { CARD_RADIUS, CARD_SX, HAIRLINE, HAIRLINE_COLOR, HAIRLINE_HOVER_COLOR, PILL_RADIUS } from './surface.js'
import { useRowSlices } from './useRowSlices.js'
import type { ActivityStatus, AgentMessage, AgentSSEEvent, ArtifactInfo, AskUserQuestionInfo, CreatedDashboardInfo, RenderedChartInfo, RenderedTableInfo } from '../types.js'
import type { AgentComposerChip, RenderPlugin } from '../plugins.js'
import type { PendingAttachment } from '../hooks/useFileAttachments.js'

export interface SidePaneMessage {
  /** Stable per message: rows are keyed by it. */
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  /** Drawn under the row, full width: a chart, a screenshot, a note. */
  extra?: React.ReactNode
}

/**
 * An agent session's conversation, drawn as the main list draws it. The values
 * are useAgentSession's own; keep their identities stable, as AgentChat's are,
 * or every row re-renders on every keystroke.
 */
export interface SidePaneConversation {
  messages: AgentMessage[]
  sessionId: string
  isStreaming: boolean
  /** What the agent is doing now: the busy line's words and the circle's state. */
  activityStatus?: ActivityStatus | null
  artifacts?: ArtifactInfo[]
  renderedTables?: ReadonlyMap<string, RenderedTableInfo>
  renderedCharts?: ReadonlyMap<string, RenderedChartInfo>
  askedQuestions?: ReadonlyMap<string, AskUserQuestionInfo>
  createdDashboards?: ReadonlyMap<string, CreatedDashboardInfo>
  /** Render plugins (Platinum's Carbon tables and charts). None: MessageRow's own cards. */
  plugins?: RenderPlugin[]
  pluginEvents?: AgentSSEEvent[]
  apiBaseUrl?: string
  authHeader?: string
  /** Stable. Records the answer to an ask_user card (useAgentSession's markQuestionAnswered). */
  onAnswerQuestion: (toolCallId: string, value: string) => void
  /** Stable. Sends an ask_user answer on as a message. */
  onSendMessage: (content: string) => void
  /** Stable. Opens an artifact. Absent: the preview's open does nothing. */
  onOpenPreview?: (artifact: ArtifactInfo) => void
}

export interface SidePaneChatProps {
  /** Keeps the unsent text in sessionStorage under this key (#1836). Absent = not kept. */
  draftKey?: string
  /** Header title. With neither a title nor onClose there is no header. */
  title?: string
  /** What the pane is scoped to; part of the title ("Insights · Page 2"). */
  scopeLabel?: string
  onClose?: () => void
  /** Shown as "Reset chat" in the header once there is a conversation. */
  onReset?: () => void
  /** Lines at the top of the conversation: what the model was told it is looking at. */
  framing?: string[]
  /**
   * The pane's greeting, the first row of the conversation ("Good morning,
   * Adam. Which file can I help you with?"). The host passes it only while
   * the pane is empty, as it does the prompts.
   */
  greeting?: string
  /** A line after the rows, before the composer: the host's question after rows of its own (the Tables summary). Shown like the greeting. */
  openingLine?: string
  /** Text rows. Ignored when `conversation` is given. */
  messages?: SidePaneMessage[]
  /** An agent session's conversation, drawn with the main list's rows (tool chains, ask_user, artifacts, plugins). */
  conversation?: SidePaneConversation
  loading: boolean
  /** Stops the running turn. Given: the composer shows Stop while loading instead of a spinner. */
  onStop?: () => void
  /** What the pane is doing while it loads (Matt, 21 Sep: a spinner should say). */
  loadingTitle?: string
  error?: string | null
  /** Suggested prompts, as pills above the composer. A click sends it. */
  prompts?: string[]
  /**
   * Send the typed text. The composer clears at once; return `false` (or a
   * promise of false) when nothing was sent, and the text is put back if the
   * reader has not started typing again. Required unless `readOnly`.
   */
  onSend?: (text: string, model: string) => void | boolean | Promise<void | boolean>
  models?: ComposerModel[]
  selectedModel?: string
  onModelChange?: (id: string) => void
  /** A control before the model picker, e.g. a persona select. */
  composerLeading?: React.ReactNode
  /** Between the conversation and the composer: a helper link, a tray. */
  aboveComposer?: React.ReactNode
  placeholder?: string
  disabled?: boolean
  /** Dictation endpoint. Default: the library's /transcribe. */
  transcribeEndpoint?: string
  /** Auth header sent with the dictation POST. */
  authHeader?: string
  /** Why the person may not dictate; set, the mic is disabled with it (audit C23). */
  dictationDisabledReason?: string
  /** Controls in the header, before the close button: a link, a toggle. */
  headerActions?: React.ReactNode
  /** A border colour for the composer card (the Help pane's blue). Absent: the usual hairline. */
  composerAccent?: string
  /** Chips in the composer's field: what the next message is about (the story board editor's selected element). */
  composerChips?: AgentComposerChip[]
  onRemoveComposerChip?: (id: string) => void
  /**
   * Bumped by the host to scroll to the newest message even when neither
   * `messages.length` nor `loading` changed (the story board editor's panel:
   * opening it should reveal the latest row, not wherever the scroll last
   * sat while it was closed).
   */
  revealKey?: number | string
  /** File attach on the composer. Absent `onFilesSelected`: no paperclip (the default — a side pane has nowhere to send a file). */
  attachments?: PendingAttachment[]
  onFilesSelected?: (files: File[]) => void
  onRemoveAttachment?: (id: string) => void
  /** The conversation alone: no composer, no prompts, no draft. */
  readOnly?: boolean
}

// Stable props for MessageRow: a fresh Map or lambda here would re-render
// every row on every keystroke.
const NOOP = () => {}
const EMPTY_MAP = new Map<string, never>()
const EMPTY_PLUGIN_MAP = new Map<number, Map<string, unknown>>()
const EMPTY_PLUGINS: RenderPlugin[] = []
const EMPTY_MODELS: ComposerModel[] = []
const NO_TIMESTAMP = ''
const NO_ATTACHMENTS: never[] = []
const NO_MESSAGES: never[] = []
const NO_AGENT_MESSAGES: AgentMessage[] = []
const NO_ARTIFACTS: ArtifactInfo[] = []
const NO_EVENTS: AgentSSEEvent[] = []
const EMPTY_INFO_MAP = new Map<string, never>()

const agentMessages = new WeakMap<SidePaneMessage, AgentMessage>()
function toAgentMessage(m: SidePaneMessage): AgentMessage {
  let out = agentMessages.get(m)
  if (!out) {
    out = { id: m.id, role: m.role === 'user' ? 'user' : 'assistant', content: m.content, timestamp: NO_TIMESTAMP }
    agentMessages.set(m, out)
  }
  return out
}

const DEFAULT_LOADING_TITLE = 'Thinking…'

// The greeting and the opening line read as the pane speaking, in the
// message list's own text size.
const GREETING_SX = { mb: 2, fontSize: '0.8125rem', lineHeight: 1.6, color: 'text.primary' } as const

export default function SidePaneChat({
  title, scopeLabel, onClose, onReset, framing, greeting, openingLine, messages = NO_MESSAGES, conversation, loading, onStop, loadingTitle, error,
  prompts, onSend, models = EMPTY_MODELS, selectedModel = '', onModelChange = NOOP,
  composerLeading, aboveComposer, placeholder = 'Ask a question…', disabled = false,
  transcribeEndpoint, authHeader, dictationDisabledReason, headerActions, composerChips, onRemoveComposerChip,
  revealKey, attachments = NO_ATTACHMENTS, onFilesSelected, onRemoveAttachment, draftKey, composerAccent,
  readOnly = false,
}: SidePaneChatProps) {
  if (import.meta.env?.DEV && !readOnly && !onSend) console.error('SidePaneChat: onSend is required unless readOnly')
  // Read-only keeps no draft: there is nothing to type into.
  const [input, setInput] = useComposerDraft(readOnly ? undefined : draftKey)
  const scrollRef = useRef<HTMLDivElement>(null)

  // The agent conversation's rows, one slice each (empty without one: hooks
  // run in order, so this runs either way).
  const agentRows = conversation?.messages ?? NO_AGENT_MESSAGES
  const rowSlices = useRowSlices({
    messages: agentRows,
    artifacts: conversation?.artifacts ?? NO_ARTIFACTS,
    renderedTables: conversation?.renderedTables ?? EMPTY_INFO_MAP,
    renderedCharts: conversation?.renderedCharts ?? EMPTY_INFO_MAP,
    askedQuestions: conversation?.askedQuestions ?? EMPTY_INFO_MAP,
    createdDashboards: conversation?.createdDashboards ?? EMPTY_INFO_MAP,
    plugins: conversation?.plugins ?? EMPTY_PLUGINS,
    pluginEvents: conversation?.pluginEvents ?? NO_EVENTS,
    isStreaming: conversation?.isStreaming ?? false,
  })
  const rowCount = conversation ? agentRows.length : messages.length

  // Keep the newest turn in view: when a message lands, when the busy line
  // comes or goes, and whenever the host bumps `revealKey` (the panel just
  // opened and should show the latest row, not an old scroll position).
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [rowCount, loading, revealKey])

  const dictation = useVoiceDictation({
    onTranscription: text => setInput(prev => (prev.trim() ? `${prev.trimEnd()} ${text}` : text)),
    transcribeEndpoint,
    authHeader,
  })
  // A pane closed mid-dictation must not leave the microphone on. The hook's
  // cancel stops the recorder; its stream is released in a state updater that
  // never runs once unmounted, so the tracks are stopped here directly.
  const dictationRef = useRef(dictation)
  dictationRef.current = dictation
  useEffect(() => () => {
    const d = dictationRef.current
    d.cancelRecording()
    d.stream?.getTracks().forEach(t => t.stop())
  }, [])

  // The living circle, with the busy line's words in its hover log.
  const agentStatus = conversation?.activityStatus ?? null
  const busyLabel = loadingTitle || agentStatus?.label || DEFAULT_LOADING_TITLE
  const status: ActivityStatus | null = !loading ? null : agentStatus && !loadingTitle ? agentStatus : { label: busyLabel, category: 'thinking' }
  const activity = useActivityLog(status, loading)

  const canSend = !loading && !disabled && !readOnly && Boolean(onSend)
  const send = (text: string) => {
    const trimmed = text.trim()
    if (!trimmed || !canSend || !onSend) return
    const result = onSend(trimmed, selectedModel)
    setInput('')
    // Not sent (the host refused it): the words go back, unless the reader
    // has already started typing something else.
    const restore = (r: void | boolean) => {
      if (r === false) setInput(prev => (prev.trim() ? prev : text))
    }
    if (result && typeof (result as Promise<void | boolean>).then === 'function') void (result as Promise<void | boolean>).then(restore, () => {})
    else restore(result as void | boolean)
  }
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    send(input)
  }
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send(input)
    }
  }

  const heading = title && scopeLabel ? `${title} · ${scopeLabel}` : title
  const showHeader = Boolean(heading || onClose || headerActions)
  const hasPrompts = !readOnly && Boolean(prompts && prompts.length > 0)
  const hasFoot = hasPrompts || Boolean(aboveComposer)
  // The composer's controls stand above its top edge. Whatever sits directly
  // above the card keeps clear of them.
  const clearOverhang = readOnly ? 2 : `${COMPOSER_OVERHANG_PX + 12}px`

  return (
    <Box data-testid="side-pane-chat" sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: 'background.paper' }}>
      {showHeader && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: HAIRLINE, flexShrink: 0 }}>
          <Typography component="h2" sx={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {heading}
          </Typography>
          {onReset && rowCount > 0 && (
            <Button
              size="small"
              color="info"
              onClick={onReset}
              disabled={loading}
              sx={{ textTransform: 'none', fontSize: 12, borderRadius: PILL_RADIUS, px: 1.25, minWidth: 0 }}
            >
              Reset chat
            </Button>
          )}
          {headerActions}
          {onClose && (
            <IconButton size="small" aria-label="Close" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      <Box
        ref={scrollRef}
        data-testid="side-pane-messages"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pt: 2,
          px: 2,
          pb: hasFoot ? 2 : clearOverhang,
        }}
      >
        {greeting && (
          <Typography data-testid="side-pane-greeting" sx={GREETING_SX}>
            {greeting}
          </Typography>
        )}

        {framing && framing.length > 0 && (
          <Box data-testid="insights-framing" sx={{ ...CARD_SX, px: 1.5, py: 1, mb: 2 }}>
            {framing.map((line, i) => (
              <Typography
                key={i}
                component="div"
                sx={{ fontSize: 12, lineHeight: 1.5, color: 'text.secondary', fontWeight: i === 0 && framing.length > 1 ? 600 : 400 }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        )}

        {conversation && agentRows.map((message, index) => (
          <MessageRow
            key={message.id}
            message={message}
            activelyThinking={conversation.isStreaming && agentStatus?.category === 'thinking' && index === agentRows.length - 1}
            showForkDivider={false}
            autoArtifacts={rowSlices[index].autoArtifacts}
            toolArtifacts={rowSlices[index].toolArtifacts}
            sessionId={conversation.sessionId}
            apiBaseUrl={conversation.apiBaseUrl ?? ''}
            authHeader={conversation.authHeader}
            isStreaming={rowSlices[index].isStreaming}
            onOpenPreview={conversation.onOpenPreview ?? NOOP}
            onAnswerQuestion={conversation.onAnswerQuestion}
            onSendMessage={conversation.onSendMessage}
            plugins={conversation.plugins ?? EMPTY_PLUGINS}
            byPlugin={rowSlices[index].byPlugin}
            toolCallTables={rowSlices[index].toolCallTables}
            toolCallCharts={rowSlices[index].toolCallCharts}
            toolCallQuestions={rowSlices[index].toolCallQuestions}
            toolCallDashboards={rowSlices[index].toolCallDashboards}
          />
        ))}

        {!conversation && messages.map(m => {
          if (m.role === 'system') {
            return (
              <Typography
                key={m.id}
                data-testid="side-pane-system"
                sx={{ mb: 2, fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}
              >
                {m.content}
              </Typography>
            )
          }
          return (
            <React.Fragment key={m.id}>
              <MessageRow
                message={toAgentMessage(m)}
                activelyThinking={false}
                showForkDivider={false}
                sessionId=""
                apiBaseUrl=""
                isStreaming={false}
                onOpenPreview={NOOP}
                onAnswerQuestion={NOOP}
                onSendMessage={NOOP}
                plugins={EMPTY_PLUGINS}
                byPlugin={EMPTY_PLUGIN_MAP}
                toolCallTables={EMPTY_MAP}
                toolCallCharts={EMPTY_MAP}
                toolCallQuestions={EMPTY_MAP}
                toolCallDashboards={EMPTY_MAP}
              />
              {m.extra != null && (
                <Box data-testid="side-pane-extra" sx={{ mt: m.content.trim() ? -1 : 0, mb: 2, minWidth: 0 }}>
                  {m.extra}
                </Box>
              )}
            </React.Fragment>
          )
        })}

        {openingLine && (
          <Typography data-testid="side-pane-opening" sx={GREETING_SX}>
            {openingLine}
          </Typography>
        )}

        {loading && (
          // The busy line: words, not a bare spinner (Matt, 21 Sep).
          <Typography
            data-testid="side-pane-busy"
            role="status"
            sx={{
              mb: 2,
              fontSize: 12,
              color: 'text.secondary',
              fontStyle: 'italic',
              '@keyframes sidePaneBusy': { '0%, 100%': { opacity: 0.55 }, '50%': { opacity: 1 } },
              animation: 'sidePaneBusy 1.6s ease-in-out infinite',
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            {busyLabel}
          </Typography>
        )}

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: CARD_RADIUS }}>{error}</Alert>}
      </Box>

      {hasFoot && (
        <Box data-testid="side-pane-foot" sx={{ px: 2, pb: clearOverhang, display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
          {hasPrompts && (
            <Box data-testid="side-pane-prompts" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {prompts!.map(p => (
                <Button
                  key={p}
                  size="small"
                  onClick={() => send(p)}
                  disabled={!canSend}
                  sx={{
                    textTransform: 'none',
                    fontSize: 12,
                    lineHeight: 1.4,
                    fontWeight: 400,
                    textAlign: 'left',
                    color: 'text.primary',
                    borderRadius: PILL_RADIUS,
                    border: `1px solid ${HAIRLINE_COLOR}`,
                    px: 1.5,
                    py: 0.5,
                    minWidth: 0,
                    '&:hover': { borderColor: HAIRLINE_HOVER_COLOR, bgcolor: 'action.hover' },
                  }}
                >
                  {p}
                </Button>
              ))}
            </Box>
          )}
          {aboveComposer}
        </Box>
      )}

      {/* The card in a bed as wide as the message column's gutter, so its
          edges line up with the rows above, as in the main conversation. */}
      {!readOnly && <Box sx={{ flexShrink: 0, px: 2 }}>
        <ComposerCard
          component="form"
          testId="side-pane-composer"
          shape="docked"
          accentColor={composerAccent}
          onSubmit={handleSubmit}
          leading={composerLeading}
          chips={composerChips}
          onRemoveChip={onRemoveComposerChip}
          models={models}
          selectedModel={selectedModel}
          onModelChange={onModelChange}
          modelDisabled={disabled}
          toolbar={
            <ChatInputToolbar
              showAttach={!!onFilesSelected}
              onFilesSelected={onFilesSelected ?? NOOP}
              attachments={attachments}
              onRemoveAttachment={onRemoveAttachment ?? NOOP}
              isRecording={dictation.isRecording}
              isTranscribing={dictation.isTranscribing}
              onToggleRecording={() => (dictation.isRecording ? dictation.stopRecording() : dictation.startRecording())}
              onStopRecording={dictation.stopRecording}
              onCancelRecording={dictation.cancelRecording}
              stream={dictation.stream}
              error={dictation.error}
              disabled={disabled}
              micDisabledReason={dictationDisabledReason}
            />
          }
          // Always drawn, as on the agent screen: resting when idle, alive
          // while the model works (Adam, 23 Sep).
          badge={!disabled ? <ActivityBadge activity={activity} /> : null}
          value={input}
          onChange={setInput}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          action={loading ? (onStop ? 'stop' : 'busy') : 'send'}
          onStop={onStop}
          sendDisabled={!input.trim() || !canSend}
        />
      </Box>}
    </Box>
  )
}
